import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext({
  lang: "de",
  setLang: () => {},
  t: (path) => path,
});

const STORAGE_KEY = "christian_lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "de";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && translations[saved]) return saved;
  const browser = (window.navigator.language || "de").slice(0, 2).toLowerCase();
  if (translations[browser]) return browser;
  return "de";
}

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) return acc[key];
    return undefined;
  }, obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = (next) => {
    if (!translations[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore quota errors
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const dict = translations[lang] || translations.de;
    const t = (path, fallback) => {
      const val = resolvePath(dict, path);
      if (val === undefined || val === null) return fallback ?? path;
      return val;
    };
    return { lang, setLang, t };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
